import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Delete as DeleteIcon, Restore as RestoreIcon } from '@mui/icons-material';
import { ProgressButton } from '@localstack/ui';
import { FeatureTuple, ProductFeature } from '@localstack/types';
import { AdminService, useApiEffect, useApiGetter } from '@localstack/services';

import {
  Box,
  Card,
  CardHeader,
  IconButton,
  List,
  ListItem,
  Chip,
  Switch,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  CardActions,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';

import { FEATURE_NAME_MAPPING } from './constants';
import { SubscriptionFeatureForm } from './SubscriptionFeatureForm';

export type SubscriptionFeaturesEditorProps = {
  disableInteraction?: boolean;
};

export const SubscriptionFeaturesEditor = ({
  disableInteraction = false,
}: SubscriptionFeaturesEditorProps): JSX.Element => {
  const [addedFeatures, setAddedFeatures] = useState<FeatureTuple[]>([]);
  const [removedFeatures, setRemovedFeatures] = useState<FeatureTuple[]>([]);
  const [displayAllFeatures, setDisplayAllFeatures] = useState<boolean>(false);

  const { orgId, subscriptionId } = useParams<'orgId' | 'subscriptionId'>() as {
    orgId: string;
    subscriptionId: string;
  };

  const { data: subscriptionFeatures, isLoading: isListing } = useApiGetter(AdminService, 'listSubscriptionFeatures', [
    orgId,
    subscriptionId,
  ]);

  const { updateSubscriptionFeatures, isLoading: isUpdating } = useApiEffect(
    AdminService,
    ['updateSubscriptionFeatures'],
    {
      revalidate: ['listSubscriptionFeatures'],
    },
  );

  const loadedDefaultFeatures = (subscriptionFeatures?.default ?? []) as FeatureTuple[];
  const loadedAddedFeatures = (subscriptionFeatures?.add ?? []) as FeatureTuple[];
  const loadedRemovedFeatures = (subscriptionFeatures?.remove ?? []) as FeatureTuple[];

  const loadedAddedFeaturesHash = loadedAddedFeatures
    .map((name, variant, version) => `${name}/${variant}/${version}`)
    .join(',');
  const loadedRemovedFeaturesHash = loadedRemovedFeatures
    .map((name, variant, version) => `${name}/${variant}/${version}`)
    .join(',');

  useEffect(() => {
    setAddedFeatures(loadedAddedFeatures);
    setRemovedFeatures(loadedRemovedFeatures);
  }, [loadedAddedFeaturesHash, loadedRemovedFeaturesHash]);

  const isFeatureRemoved = (featureTuple: FeatureTuple): boolean =>
    !!removedFeatures.find((featureTuple_) => featureTuple_.join('/') === featureTuple.join('/'));

  const handleAddAddedFeature = (feature: FeatureTuple) => {
    setAddedFeatures((prev) => [...prev, feature]);
  };

  const handleAddRemmovedFeature = (feature: FeatureTuple) => {
    setRemovedFeatures((prev) => [...prev, feature]);
  };

  const handleRemoveAddedFeature = (feature: FeatureTuple) => {
    setAddedFeatures((prev) => prev.filter((addedFeature) => addedFeature.join('/') !== feature.join('/')));
  };

  const handleRemoveRemovedFeature = (feature: FeatureTuple) => {
    setRemovedFeatures((prev) => prev.filter((removedFeature) => removedFeature.join('/') !== feature.join('/')));
  };

  const filteredDefaultFeatures = displayAllFeatures
    ? loadedDefaultFeatures
    : loadedDefaultFeatures.filter((feature) => isFeatureRemoved(feature));

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            Features
            {(isListing || isUpdating) && (
              <Box ml={1}>
                <CircularProgress color="info" size={14} />
              </Box>
            )}
          </Box>
        }
        action={
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={displayAllFeatures}
                onChange={(_, checked: boolean) => setDisplayAllFeatures(checked)}
              />
            }
            label="Display Default Features"
          />
        }
      />
      <List>
        <ListItem>
          <SubscriptionFeatureForm onAddFeature={handleAddAddedFeature} />
        </ListItem>
        {filteredDefaultFeatures.map((feature) => (
          <ListItem key={feature.join('/')} button>
            <ListItemAvatar>
              <Box mr={2}>
                <Chip label="Default" variant="filled" color="default" size="small" />
              </Box>
            </ListItemAvatar>
            <ListItemText
              primary={`${FEATURE_NAME_MAPPING[feature[0] as ProductFeature]} (${feature[0]})`}
              secondary={`Variant: ${feature[1] || 'None'} @ Version: ${feature[2]}`}
              primaryTypographyProps={{
                color: isFeatureRemoved(feature) ? 'error' : 'textPrimary',
                style: {
                  textDecoration: isFeatureRemoved(feature) ? 'line-through' : 'none',
                },
              }}
              secondaryTypographyProps={{
                color: isFeatureRemoved(feature) ? 'error' : 'textPrimary',
                style: {
                  textDecoration: isFeatureRemoved(feature) ? 'line-through' : 'none',
                },
              }}
            />
            <ListItemSecondaryAction>
              {isFeatureRemoved(feature) ? (
                <IconButton onClick={() => handleRemoveRemovedFeature(feature)}>
                  <RestoreIcon />
                </IconButton>
              ) : (
                <IconButton onClick={() => handleAddRemmovedFeature(feature)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        {addedFeatures.map((feature) => (
          <ListItem key={feature.join('/')} button>
            <ListItemAvatar>
              <Box mr={2}>
                <Chip label="Custom" variant="filled" color="secondary" size="small" />
              </Box>
            </ListItemAvatar>
            <ListItemText
              primary={`${FEATURE_NAME_MAPPING[feature[0] as ProductFeature]} (${feature[0]})`}
              secondary={`Variant: ${feature[1] || 'None'} @ Version: ${feature[2]}`}
              primaryTypographyProps={{ color: 'success.main' }}
              secondaryTypographyProps={{ color: 'success.main' }}
            />
            <ListItemSecondaryAction>
              <IconButton onClick={() => handleRemoveAddedFeature(feature)}>
                <DeleteIcon />
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
      <CardActions>
        <ProgressButton
          loading={isUpdating}
          disabled={disableInteraction}
          variant="contained"
          color="primary"
          onClick={() => {
            updateSubscriptionFeatures(orgId, subscriptionId, {
              add: addedFeatures,
              remove: removedFeatures,
            });
          }}
        >
          Save
        </ProgressButton>
      </CardActions>
    </Card>
  );
};
